import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { cloneDeep } from 'lodash';
import * as moment from 'moment';
import { isValidLayout } from 'src/app/sakp/model/fleetmanagement/vehicle-doors-spec';
import { DECLARED_ICONS } from 'src/app/top-bar/util/message-util';

export function presentDateValidator(control: AbstractControl): ValidationErrors | null {
  if (control.value && control.valid) {
    if (new Date() > control.value)
      return { dateBeforeNow: true };
  }
  return null;
}


export function presentPlusMinutesDateValidator(control: AbstractControl, minutes: number): ValidationErrors | null {
  if (control.value && control.valid) {
    const date = moment(new Date()).add(minutes, 'm').toDate();
    if (date > control.value)
      return { dateBeforeInterval: true };
  }
  return null;
}

// Crossow-y walidator dla grupy;
export function startDateAfterEnd(control: AbstractControl): ValidationErrors | null {
  const startCtrl = control.get('start');
  const endCtrl = control.get('end');
  if (!startCtrl || !endCtrl) return null;

  const start = startCtrl.value;
  const end = endCtrl.value;

  if (!start || !end) {
    return null;
  }

  if (start > end) {
    const existingErrors = endCtrl.errors || {};
    endCtrl.setErrors({ ...existingErrors, startDateMustBeBeforeEndDate: true });
    return { startAfterEnd: true };
  } else {
    const errors = endCtrl.errors;
    if (errors) {
      delete errors.startDateMustBeBeforeEndDate;
      if (Object.keys(errors).length === 0) {
        endCtrl.setErrors(null);
      } else {
        endCtrl.setErrors(errors);
      }
    }
    return null;
  }
}

export function maximumDaysRange(control: AbstractControl, maximumDays: number): ValidationErrors | null {
  const endDateResult = control.parent.get('end');
  const startDateResult = control.parent.get('start');
  if (startDateResult.value != null && endDateResult.value != null
    && endDateResult.value.isAfter(cloneDeep(startDateResult.value).add(maximumDays, 'days'))) {
    return { maximumDaysRange: true };
  } else if (startDateResult.value != null && endDateResult.value != null) {
    endDateResult.markAsPending({ emitEvent: true });
    startDateResult.markAsPending({ emitEvent: true });
  }
  return null;
}

export function uniqueNameValidator(control: AbstractControl, names: string[]): ValidationErrors | null {
  if (control.value && control.valid) {
    if (names.includes(control.value)) {
      control.markAsTouched();
      return { uniqueName: true };
    }
  }
  return null;
}

export function anyFieldsTrueValidatorFormArray(path?: string): ValidatorFn {
  return (control: AbstractControl) => {
    if (!(control instanceof FormArray))
      return null;

    for (const group of control.controls) {
      if (group instanceof FormGroup) {
        const fieldValue = path ? group.get(path)?.value : null;

        if (path && fieldValue === true) {
          return null;
        }

        if (!path) {
          for (const key of Object.keys(group.controls)) {
            if (group.controls[key].value === true) {
              return null;
            }
          }
        }
      }
    }
    return { anyTrueValue: true };
  };
}


export function anyFieldsTrueValidator(): ValidatorFn {
  return (group: FormGroup) => {
    for (const key of Object.keys(group.controls)) {
      if (group.controls[key].value === true) return null;
    }
    return { anyTrueValue: false };
  };
}

export function stateValidator(control: AbstractControl, state: boolean): ValidationErrors | null {
  if (control.value && !state) {
    control.markAsTouched();
    return { invalid: true };
  }
  return null;
}

export function messageTypeValidator(control: AbstractControl): ValidationErrors | null {
  if (control.value && control.valid) {
    if (!DECLARED_ICONS.map(ic => ic.icon).includes(control.value)) {
      control.markAsTouched();
      return { invalidType: true };
    }
  }
  return null;
}

export function notAllowedCharValidator(control: AbstractControl, notAllowedChars: string): ValidationErrors | null {
  if (control.value && control.valid) {
    const notAllowed = Array.from(control.value as string).filter(char => notAllowedChars.includes(char));
    if (notAllowed?.length) {
      control.markAsTouched();
      return { notAllowedChars: `${notAllowed}` };
    }
  }
  return null;
}

export function duplicateControlValueValidator(secondControl: string): ValidatorFn {
  return (control: AbstractControl) => {
    const second = control.parent?.get(secondControl);
    const secondValue = second?.value;
    const firstValue = control.value;
    if (firstValue !== null && firstValue !== undefined && control.valid && secondValue && second?.valid) {
      if (firstValue === secondValue) {
        control.markAsTouched();
        return { duplicateControlValue: true };
      }
    }
    return null;
  };
}

export function numericPrecisionValidator(maxDigits: number, decimalPlaces: number): ValidatorFn {
  const regex = new RegExp(`^\\d{1,${maxDigits}}(\\.\\d{0,${decimalPlaces}})?$`);

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const textValue = value.toString();

    if (!regex.test(textValue)) {
      control.markAsTouched();
      return { numericPrecisionError: { maxDigits, decimalPlaces, actual: value } };
    }
    return null;
  };
}

export function positiveIntegerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {

    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }
    const pattern = /^[1-9]\d*$/;
    if (pattern.test(value.toString()))
      return null;
    control.markAsTouched();
    return { notPositiveInteger: { actual: value } };
  };
}

export function doorsLayoutValidator(): ValidatorFn {

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (!isValidLayout(value)) {
      control.markAsTouched();
      return { doorsLayoutError: { actual: value } };
    }
    return null;
  };
}

export function crossFieldDateValidator(maxMin: number, from: string, to: string): ValidatorFn {
  return (form: AbstractControl): ValidationErrors | null => {
    const start = form.get(from)?.value;
    const end = form.get(to)?.value;
    if (!start || !end) return null;

    const errors: any = {};
    if (new Date(start) > new Date(end)) {
      errors.startAfterEnd = true;
    }

    const diff = (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 );
    if (diff > maxMin) {
      errors.maxDaysExceeded = true;
    }

    return Object.keys(errors).length ? errors : null;
  };
}
